/**
 * Phase 24.4b Step 4 — typed wrappers for `/api/meal-plan/*`.
 *
 * Types mirror backend Pydantic schemas in `app/schemas/meal_plan.py`
 * verbatim (Phase 22.3.7 + Phase 24.1 per-item targets + Phase 24.3
 * action endpoints). When backend schema changes, update both sides in
 * lock-step.
 *
 * Backend signal / frontend voice: `selection_reason` is emitted as
 * `{key, params}` pairs — never a pre-rendered string. Frontend resolves
 * via i18next `t(key, params)` so direction/severity composition stays
 * locale-stable and locale-cheap.
 */
import { api } from "./client";
import type { RecipeRead } from "./recipes";

// ---------------------------------------------------------------------------
// Types — mirror app/schemas/meal_plan.py
// ---------------------------------------------------------------------------

/**
 * `{key, params}` pair for backend-emitted, frontend-resolved
 * translation strings. Mirrors the dict literals in
 * app/services/recipe_selector.py and downstream emitters.
 */
export interface SelectionReason {
  key: string;
  params: Record<string, unknown>;
}

export interface MealPlanItemRead {
  id: number;
  date: string; // ISO date "YYYY-MM-DD"
  meal_type: string;
  servings: number;
  planned_kcal: number;
  planned_protein_g: number;
  planned_carbs_g: number;
  planned_fat_g: number;
  // Phase 24.1 — per-item targets the planner allocated. NULL on legacy
  // plans (pre-Phase-24.1); UI may show planned_* alone in that case.
  target_kcal: number | null;
  target_protein_g: number | null;
  target_carbs_g: number | null;
  target_fat_g: number | null;
  day_profile: string;
  selection_reason: SelectionReason | null;
  is_cooked: boolean;
  cooked_at: string | null; // ISO datetime
  recipe: RecipeRead;
}

export type FitStatus = "ok" | "warn" | "fail";

export interface DayTotals {
  date: string; // ISO date
  day_profile: string;
  // Phase 24.1 — float|None: legacy plans without per-item targets fall
  // back to plan.target_calories_avg (still rendered as int). NULL only
  // on degenerate empty plans.
  target_kcal: number | null;
  planned_kcal: number;
  planned_protein_g: number;
  planned_carbs_g: number;
  planned_fat_g: number;
  fit_status: FitStatus;
  items: MealPlanItemRead[];
}

export interface MealPlanRead {
  id: number;
  week_start_date: string; // ISO date
  status: string;
  target_calories_avg: number;
  macro_mode: string;
  generated_at: string; // ISO datetime
  days: DayTotals[];
  warnings: string[];
  notes: Record<string, unknown>;
}

export interface GenerateRequest {
  week_start_date: string; // ISO date
}

// Phase 24.3 — action endpoint schemas

export interface SwapAlternative {
  recipe: RecipeRead;
  servings: number;
  score: number;
  actual_kcal: number;
  actual_protein_g: number;
  actual_carbs_g: number;
  actual_fat_g: number;
  selection_reason: SelectionReason | null;
}

export interface SwapOptionsResponse {
  plan_id: number;
  item_id: number;
  current_recipe: RecipeRead;
  alternatives: SwapAlternative[];
}

export interface SwapRequest {
  recipe_id: number;
}

export interface ShoppingListChanges {
  rows_created: number;
  rows_preserved_state: number;
  warnings: string[];
}

export interface SwapResponse {
  item: MealPlanItemRead;
  shopping_list_changes: ShoppingListChanges;
}

export interface MarkCookedRequest {
  is_cooked: boolean;
}

export interface MarkCookedResponse {
  item_id: number;
  is_cooked: boolean;
  cooked_at: string | null; // ISO datetime
}

// ---------------------------------------------------------------------------
// Endpoints — `/api/meal-plan/*`
// ---------------------------------------------------------------------------

export async function generateMealPlan(
  weekStartDate: string,
): Promise<MealPlanRead> {
  const { data } = await api.post<MealPlanRead>("/meal-plan/generate", {
    week_start_date: weekStartDate,
  } satisfies GenerateRequest);
  return data;
}

export async function getCurrentMealPlan(): Promise<MealPlanRead> {
  const { data } = await api.get<MealPlanRead>("/meal-plan/current");
  return data;
}

export async function getMealPlan(planId: number): Promise<MealPlanRead> {
  const { data } = await api.get<MealPlanRead>(`/meal-plan/${planId}`);
  return data;
}

export async function getSwapOptions(
  planId: number,
  itemId: number,
): Promise<SwapOptionsResponse> {
  const { data } = await api.get<SwapOptionsResponse>(
    `/meal-plan/${planId}/items/${itemId}/swap-options`,
  );
  return data;
}

export async function swapMealPlanItem(
  planId: number,
  itemId: number,
  recipeId: number,
): Promise<SwapResponse> {
  const { data } = await api.post<SwapResponse>(
    `/meal-plan/${planId}/items/${itemId}/swap`,
    { recipe_id: recipeId } satisfies SwapRequest,
  );
  return data;
}

export async function markMealPlanItemCooked(
  planId: number,
  itemId: number,
  isCooked: boolean,
): Promise<MarkCookedResponse> {
  const { data } = await api.post<MarkCookedResponse>(
    `/meal-plan/${planId}/items/${itemId}/mark-cooked`,
    { is_cooked: isCooked } satisfies MarkCookedRequest,
  );
  return data;
}

// Phase 24.7.4 T4 — per-recipe servings adjustment.
// Allowed values mirror backend _ALLOWED_SERVINGS set in api/meal_plan.py.
export const ALLOWED_SERVINGS = [0.5, 1.0, 1.5, 2.0, 3.0] as const;
export type AllowedServings = (typeof ALLOWED_SERVINGS)[number];

export async function updateMealPlanItemServings(
  planId: number,
  itemId: number,
  servings: AllowedServings,
): Promise<MealPlanItemRead> {
  const { data } = await api.patch<MealPlanItemRead>(
    `/meal-plan/${planId}/items/${itemId}/servings`,
    { servings },
  );
  return data;
}

// ---------------------------------------------------------------------------
// SOURCE OF TRUTH: backend app/schemas/meal_plan.py compute_fit_status
// Thresholds duplicated here for optimistic UI updates only.
// If backend changes thresholds, update this file in same commit.
// Backend test test_compute_fit_status_thresholds_documented validates
// ground truth (added in S4a.2 Step 8 batch).
// ---------------------------------------------------------------------------
export function computeFitStatus(
  plannedKcal: number,
  targetKcal: number,
): FitStatus {
  if (!targetKcal || targetKcal <= 0) return "fail";
  const deviation = Math.abs(plannedKcal - targetKcal) / targetKcal;
  if (deviation < 0.05) return "ok";
  if (deviation < 0.1) return "warn";
  return "fail";
}
